import { useState, useEffect } from 'react';
import { ApiError, storedFileSrc } from '../lib/api';
import { brandingApi, LOGIN_LAYOUTS, type Branding } from '../lib/brandingApi';

/**
 * Tenant-admin self-service for the school's look: colours, logo, support email, tagline and the
 * login-screen layout. The Tenant row already drove branding end-to-end (public branding → CSS
 * variables → login page); this is the write side, so a school changes its own look without ringing us.
 */
export function BrandingPage() {
  const [b, setB] = useState<Branding | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { brandingApi.get().then(setB).catch(() => setB(null)); }, []);

  const save = async () => {
    if (!b) return;
    setBusy(true); setMsg(null);
    try {
      const saved = await brandingApi.update({
        primaryColor: b.primaryColor,
        accentColor: b.accentColor,
        supportEmail: b.supportEmail,
        tagline: b.tagline,
        loginLayout: b.loginLayout,
      });
      setB(saved);
      setMsg('Saved. Refresh to see it applied across the app.');
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const upLogo = async (file: File) => {
    setBusy(true); setMsg(null);
    try { setB(await brandingApi.uploadLogo(file)); setMsg('Logo updated.'); }
    catch { setMsg('Could not upload the logo.'); }
    finally { setBusy(false); }
  };

  const upLoginImg = async (file: File) => {
    setBusy(true); setMsg(null);
    try { setB(await brandingApi.uploadLoginImage(file)); setMsg('Login image updated.'); }
    catch { setMsg('Could not upload the image.'); }
    finally { setBusy(false); }
  };

  if (!b) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  // ⚠ storedFileSrc, not apiUrl — an already-absolute logo URL must be left alone. This page used
  // apiUrl directly and hid the crest it had just uploaded.
  const imgSrc = storedFileSrc;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Settings</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Branding</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          Your school's colours, logo and login screen. Changes apply to everyone the next time they
          load the app.
        </p>
      </div>

      {msg && <div className="pill" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 14, fontSize: 13 }}>{msg}</div>}

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', maxWidth: 820 }}>
        {/* colours + identity */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>Colours & identity</div>

          <label className="label">Primary colour</label>
          <ColorRow value={b.primaryColor} onChange={(v) => setB({ ...b, primaryColor: v })} />

          <label className="label" style={{ marginTop: 12 }}>Accent colour</label>
          <ColorRow value={b.accentColor} onChange={(v) => setB({ ...b, accentColor: v })} />

          <label className="label" style={{ marginTop: 12 }}>Support email</label>
          <input className="input" value={b.supportEmail ?? ''} placeholder="office@school.edu"
            onChange={(e) => setB({ ...b, supportEmail: e.target.value })} />

          <label className="label" style={{ marginTop: 12 }}>Tagline (shown on the login screen)</label>
          <input className="input" value={b.tagline ?? ''} placeholder="Excellence since 1982"
            onChange={(e) => setB({ ...b, tagline: e.target.value })} />

          <button className="btn btn-accent" style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
            disabled={busy} onClick={save}>Save changes</button>
        </div>

        {/* logo + login screen */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14 }}>Logo & login screen</div>

          <label className="label">Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {imgSrc(b.logoUrl)
              ? <img src={imgSrc(b.logoUrl)!} alt="logo" style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: 8, background: '#fff', border: '1px solid var(--line)' }} />
              : <div style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--surface-2,#f4f4f2)', display: 'grid', placeItems: 'center', fontSize: 11, color: 'var(--ink-3)' }}>none</div>}
            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upLogo(f); }} />
          </div>

          <label className="label">Login layout</label>
          <select className="input" value={b.loginLayout} onChange={(e) => setB({ ...b, loginLayout: e.target.value })} style={{ marginBottom: 14 }}>
            {LOGIN_LAYOUTS.map((l) => <option key={l} value={l}>{layoutLabel(l)}</option>)}
          </select>

          <label className="label">Login background image (used by the split & centered layouts)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {imgSrc(b.loginImageUrl)
              ? <img src={imgSrc(b.loginImageUrl)!} alt="login" style={{ width: 80, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' }} />
              : <div style={{ width: 80, height: 52, borderRadius: 8, background: 'var(--surface-2,#f4f4f2)', display: 'grid', placeItems: 'center', fontSize: 11, color: 'var(--ink-3)' }}>none</div>}
            <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upLoginImg(f); }} />
          </div>

          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 12 }}>
            Tip: the login layout and background are what a parent sees first. "Split" shows your photo
            beside the sign-in card; "Minimal" is just your logo and the form.
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: 40, height: 34, padding: 0, border: '1px solid var(--line)', borderRadius: 6, cursor: 'pointer' }} />
      <input className="input mono" value={value} onChange={(e) => onChange(e.target.value)} style={{ flex: 1 }} />
    </div>
  );
}

function layoutLabel(l: string): string {
  return l === 'SplitImage' ? 'Split — image beside the form'
    : l === 'Minimal' ? 'Minimal — logo + form only'
    : 'Centered — card on a coloured background';
}
